/**
 * SelectionService - Centralized singleton for selection management.
 *
 * Pure TypeScript service (no Vue dependencies) that:
 * 1. Owns the SelectionSet instance
 * 2. Provides a global event system for selection changes
 * 3. Exposes programmatic selection API
 * 4. Delegates grid operations to SelectionSet
 */

import { SelectionSet, type SelectionSetContext } from "./SelectionSet";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { Grid } from "../transform/Grid";
import { HANDLE_SIZES } from "../transform/types";
import {
  DeleteEntityCommand,
  type DeleteEntityParams,
} from "../commands/DeleteEntityCommand";
import { AddEntityCommand } from "../commands/AddEntityCommand";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { ensureLayerExists } from "../layers/utils";
import {
  deepCloneEntities,
  getCombinedExtents,
  createTranslationMatrix,
  type ClonedEntityInfo,
} from "../entities/cloning";
import { HatchReactorManager } from "../managers/HatchReactorManager";
import { noteSelectionChanged } from "../telemetry/updateTelemetry";

/**
 * Handler type for selection change events.
 */
export type SelectionChangeHandler = (handles: string[]) => void;

/**
 * SelectionService singleton.
 * Centralizes selection management across the drawing system.
 */
export class SelectionService {
  private static instance: SelectionService | null = null;
  private _selectionSet: SelectionSet;
  private _changeHandlers = new Set<SelectionChangeHandler>();
  private _isContextSet = false;
  private _ctx: SelectionSetContext | null = null;

  private constructor() {
    this._selectionSet = new SelectionSet();
  }

  /**
   * Get the singleton instance.
   */
  static getInstance(): SelectionService {
    if (!SelectionService.instance) {
      SelectionService.instance = new SelectionService();
    }
    return SelectionService.instance;
  }

  /**
   * Reset the singleton (for testing or reinitialization).
   */
  static reset(): void {
    if (SelectionService.instance) {
      SelectionService.instance.dispose();
      SelectionService.instance = null;
    }
  }

  // ==================== Context ====================

  /**
   * Set the context with all required dependencies.
   * Must be called after opening/creating a file.
   */
  setContext(ctx: SelectionSetContext): void {
    this._selectionSet.setContext(ctx);
    this._ctx = ctx;
    this._isContextSet = true;
  }

  /**
   * Check if context has been set.
   */
  get hasContext(): boolean {
    return this._isContextSet;
  }

  // ==================== Plain Getters (not reactive) ====================

  /**
   * Get the number of selected entities.
   */
  getCount(): number {
    return this._selectionSet.count;
  }

  /**
   * Get the number of selected entities (getter alias).
   */
  get count(): number {
    return this._selectionSet.count;
  }

  /**
   * Check if selection is empty.
   */
  isEmpty(): boolean {
    return this._selectionSet.isEmpty;
  }

  /**
   * Get all selected entity handles.
   */
  getAllHandles(): string[] {
    return this._selectionSet.getAllHandles();
  }

  /**
   * Get all selected entity handles (getter alias).
   */
  get handles(): string[] {
    return this._selectionSet.getAllHandles();
  }

  // ==================== Selection API ====================

  /**
   * Get the underlying SelectionSet instance.
   * Use this for direct access to selection set methods.
   */
  getSelectionSet(): SelectionSet {
    return this._selectionSet;
  }

  /**
   * Get all selected entity IDs as an array.
   */
  getAll(): DrawingModule.OdDbObjectId[] {
    return this._selectionSet.getAll();
  }

  /**
   * Check if an entity is in the selection.
   */
  has(entityId: DrawingModule.OdDbObjectId): boolean {
    return this._selectionSet.has(entityId);
  }

  /**
   * Get the native ODA selection set.
   */
  getNativeSelectionSet(): DrawingModule.OdDbSelectionSet | null {
    return this._selectionSet.getNativeSelectionSet();
  }

  // ==================== Mutation Methods (notify on change) ====================

  /**
   * Set selection from a native OdDbSelectionSet.
   * Dehighlights previous selection and highlights new one.
   * @param topmostOnly Keep only the topmost selectable entity (single click).
   */
  setFromNative(
    newSelectionSet: DrawingModule.OdDbSelectionSet | null,
    topmostOnly = false,
  ): void {
    this._selectionSet.setFromNative(newSelectionSet, topmostOnly);
    this.notifyChange();
  }

  /**
   * Add entities from another selection set to current selection.
   */
  addFromNative(
    additionalSet: DrawingModule.OdDbSelectionSet,
    topmostOnly = false,
  ): void {
    this._selectionSet.addFromNative(additionalSet, topmostOnly);
    this.notifyChange();
  }

  /**
   * Clear all selections and update the viewer.
   */
  clear(): void {
    this._selectionSet.clear();
    this.notifyChange();
    // Note: viewer.update() is not called here - callers should update the viewer if needed
  }

  /**
   * Remove an entity from selection.
   */
  remove(entityId: DrawingModule.OdDbObjectId): void {
    this._selectionSet.remove(entityId);
    this.notifyChange();
  }

  removeMany(entityIds: DrawingModule.OdDbObjectId[]) {
    for (const entityId of entityIds) {
      this.remove(entityId);
    }
  }

  /**
   * Add a single entity to the selection (uses SelectionSet.add internally).
   * @param entityId The entity to add
   * @param updateGrid Whether to update the transform grid (default: true)
   */
  add(entityId: DrawingModule.OdDbObjectId, updateGrid: boolean = true): void {
    this._selectionSet.add(entityId, updateGrid);
    this.notifyChange();
  }

  // ==================== Programmatic Selection ====================

  /**
   * Programmatically select a single entity.
   * Clears existing selection and selects the specified entity.
   *
   * @param entityId The entity to select
   * @param module The drawing module
   * @param db The database
   */
  selectEntity(
    entityId: DrawingModule.OdDbObjectId,
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
  ): void {
    // Clear existing selection first
    this._selectionSet.clear();

    // Create a new selection set with just this entity
    const newSelection = module.OdDbSelectionSet.createObject1(db);
    const method = newSelection.method(entityId);
    newSelection.append(entityId, method);

    // Set the selection (this highlights and updates the grid)
    this._selectionSet.setFromNative(newSelection);
    this.notifyChange();
    noteSelectionChanged(1, "selectEntity");
  }

  /**
   * Programmatically select multiple entities.
   * Clears existing selection and selects the specified entities.
   * ******* Important ************ Remember to delete ids once entities are added to selectionSet
   * @param entityIds Array of entities to select
   * @param module The drawing module
   * @param db The database
   */
  selectEntities(
    entityIds: DrawingModule.OdDbObjectId[],
    module: DrawingModuleType,
    db: DrawingModule.OdDbDatabase,
  ): void {
    if (entityIds.length === 0) {
      this.clear();
      return;
    }

    // Clear existing selection first
    this._selectionSet.clear();

    // Create a new selection set with all entities
    const newSelection = module.OdDbSelectionSet.createObject1(db);
    for (const entityId of entityIds) {
      const method = newSelection.method(entityId);
      newSelection.append(entityId, method);
    }

    // Set the selection (this highlights and updates the grid)
    this._selectionSet.setFromNative(newSelection);
    this.notifyChange();
    noteSelectionChanged(entityIds.length, "selectEntities");
  }

  // ==================== Zoom & Highlight ====================

  /** bbox monde de l'union des entités des handles, ou null. */
  private unionBbox(
    handles: string[],
  ): { minX: number; minY: number; maxX: number; maxY: number } | null {
    if (!this._ctx) return null;
    const { module, db } = this._ctx;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let has = false;
    for (const h of handles) {
      const id = findEntityByHandle(module, db, h);
      if (!id) continue;
      try {
        const ent = openAs(id, module.OdDbEntity, OpenMode.kForRead);
        const ext = new module.OdGeExtents3d();
        ent.getGeomExtents(ext);
        if (ext.isValidExtents()) {
          const mn = ext.minPoint();
          const mx = ext.maxPoint();
          if (mn.x < minX) minX = mn.x;
          if (mn.y < minY) minY = mn.y;
          if (mx.x > maxX) maxX = mx.x;
          if (mx.y > maxY) maxY = mx.y;
          has = true;
          deleteObj(mn);
          deleteObj(mx);
        }
        deleteObj(ext);
        deleteObj(ent);
      } catch {
        // ignore
      }
      deleteObj(id);
    }
    return has ? { minX, minY, maxX, maxY } : null;
  }

  /** Zoome le plan sur les entités des handles, SANS modifier la sélection. */
  zoomToHandles(handles: string[]): number {
    if (!this._ctx) return 0;
    const box = this.unionBbox(handles);
    if (!box) return 0;
    const pad = Math.max(box.maxX - box.minX, box.maxY - box.minY, 2);
    const { viewer } = this._ctx;
    viewer.zoomWindow(
      box.minX - pad,
      box.minY - pad,
      box.maxX + pad,
      box.maxY + pad,
    );
    viewer.update();
    return handles.length;
  }

  /**
   * Sélectionne les entités des handles SANS toucher à la vue — pour mettre
   * en évidence un élément déjà visible (ex. une entité d'un groupe d'analyse
   * dont le cadre est à l'écran) sans désorienter l'utilisateur.
   */
  selectByHandles(handles: string[]): number {
    if (!this._ctx) return 0;
    const { module, db, viewer } = this._ctx;

    const ids: DrawingModule.OdDbObjectId[] = [];
    for (const h of handles) {
      const id = findEntityByHandle(module, db, h);
      if (id) ids.push(id);
    }
    if (ids.length === 0) {
      this.clear();
      viewer.update();
      return 0;
    }

    this.selectEntities(ids, module, db);
    viewer.update();
    // selectEntities a copié dans le set natif — on libère les ids.
    for (const id of ids) deleteObj(id);
    return ids.length;
  }

  /** Surligne (sélectionne) les entités des handles ET zoome dessus (avec marge). */
  highlightByHandles(handles: string[]): number {
    if (!this._ctx) return 0;
    const { module, db, viewer } = this._ctx;

    const ids: DrawingModule.OdDbObjectId[] = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let hasBox = false;

    for (const h of handles) {
      const id = findEntityByHandle(module, db, h);
      if (!id) continue;
      ids.push(id);
      try {
        const ent = openAs(id, module.OdDbEntity, OpenMode.kForRead);
        const ext = new module.OdGeExtents3d();
        ent.getGeomExtents(ext);
        if (ext.isValidExtents()) {
          const mn = ext.minPoint();
          const mx = ext.maxPoint();
          if (mn.x < minX) minX = mn.x;
          if (mn.y < minY) minY = mn.y;
          if (mx.x > maxX) maxX = mx.x;
          if (mx.y > maxY) maxY = mx.y;
          hasBox = true;
          deleteObj(mn);
          deleteObj(mx);
        }
        deleteObj(ext);
        deleteObj(ent);
      } catch {
        // extents illisibles — on ignore pour le zoom
      }
    }

    if (ids.length === 0) {
      this.clear();
      viewer.update();
      return 0;
    }

    this.selectEntities(ids, module, db);

    if (hasBox) {
      const pad = Math.max(maxX - minX, maxY - minY, 2);
      viewer.zoomWindow(minX - pad, minY - pad, maxX + pad, maxY + pad);
      // La grille (bbox/poignées) a été construite AVANT le zoom, en coordonnées
      // écran désormais périmées — sans redraw elle n'apparaît qu'à la prochaine
      // interaction de vue.
      this.redrawGrid();
    }

    viewer.update();
    // selectEntities a copié dans le set natif — on libère les ids.
    for (const id of ids) deleteObj(id);
    return ids.length;
  }

  // ==================== Events (plain callbacks, no Vue) ====================

  /**
   * Subscribe to selection changes.
   * @param handler Function called with array of selected handles
   * @returns Unsubscribe function
   */
  onChange(handler: SelectionChangeHandler): () => void {
    this._changeHandlers.add(handler);
    return () => {
      this._changeHandlers.delete(handler);
    };
  }

  /**
   * Notify all selection change handlers.
   */
  private notifyChange(): void {
    const handles = this._selectionSet.getAllHandles();
    // Convert Set to array for iteration (avoids downlevelIteration requirement)
    const handlers = Array.from(this._changeHandlers);
    for (const handler of handlers) {
      handler(handles);
    }
  }

  // ==================== Grid Delegation ====================

  /**
   * Update the transform grid based on current selection.
   */
  updateGrid(): void {
    this._selectionSet.updateGrid();
  }

  /**
   * Redraw the transform grid (call after view changes).
   */
  redrawGrid(): void {
    this._selectionSet.redrawGrid();
  }

  /**
   * Hide the transform grid during a touch navigation (pinch, pan), redraw
   * it when the navigation ends.
   */
  setNavigating(navigating: boolean): void {
    this._selectionSet.setNavigating(navigating);
  }

  /**
   * Get the transform grid.
   */
  getGrid(): Grid | null {
    return this._selectionSet.getGrid();
  }

  /**
   * Clear the transform grid (used when selection is cleared).
   */
  clearTransformGrid(): void {
    this._selectionSet.getGrid()?.clear();
  }

  /**
   * Check if a transform operation is in progress.
   */
  get isTransforming(): boolean {
    return this._selectionSet.isTransforming;
  }

  /**
   * Cancel the current transform operation.
   */
  cancelTransform(): void {
    this._selectionSet.cancelTransform();
  }

  // ==================== Highlighting ====================

  /**
   * Highlight or dehighlight all entities in selection.
   */
  highlightAll(doHighlight: boolean): void {
    this._selectionSet.highlightAll(doHighlight);
  }

  // ==================== Layer Query ====================

  /**
   * Get the layer names of all selected entities.
   */
  getSelectedLayers(): string[] {
    return this._selectionSet.getSelectedLayers();
  }

  // ==================== High-Level Operations ====================

  /**
   * Sets the scale used to draw handles relative to base.
   * @param scale Scale multiplier
   */
  set handleScale(scale: number) {
    HANDLE_SIZES.handleScale = scale;
  }

  /**
   * Delete all selected entities.
   * Uses soft-delete pattern for undo/redo support.
   */
  deleteSelected(): void {
    const ctx = this._selectionSet.getContext();
    if (!ctx || !ctx.viewer?.database || !ctx.history) return;

    const entityIds = this._selectionSet.getAll();
    if (entityIds.length === 0) return;

    const module = ctx.viewer.module;

    // Dehighlight first
    this._selectionSet.highlightAll(false);

    // Build delete params with original layer names
    const deleteParams: DeleteEntityParams[] = [];

    for (const entityId of entityIds) {
      if (entityId && !entityId.isNull()) {
        // Get original layer name before deleting
        const entity = openAs(entityId, module.OdDbEntity, 0); // kForRead
        const layerOdStr = entity.layer();
        const layerName = layerOdStr.toString();
        deleteObj(layerOdStr);
        deleteObj(entity);

        deleteParams.push({
          entityId,
          originalLayerName: layerName,
        });
      }
    }

    if (deleteParams.length > 0) {
      // Create and execute delete command (uses soft-delete to hidden layer)
      const command = new DeleteEntityCommand(ctx.viewer, deleteParams);
      ctx.history.execute(command);
    }

    // Clear selection
    this.clear();

    // Update view
    ctx.viewer.update();
  }

  /**
   * Clone the given entities (plus any associated hatches) at an absolute
   * offset from their current position, with undo registration via
   * AddEntityCommand. Does NOT touch the current selection set — callers
   * needing "clear old selection, select new clones" (the instant-copy UX)
   * must do that themselves. See duplicateSelected() below for that, and
   * CopyDragger for a caller that places multiple independent clones at
   * different offsets from the same original entities without ever
   * re-reading the (possibly now-stale) selection set in between.
   */
  cloneEntitiesAtOffset(
    entityIds: DrawingModule.OdDbObjectId[],
    offsetX: number,
    offsetY: number,
  ): ClonedEntityInfo[] {
    const ctx = this._selectionSet.getContext();
    if (!ctx || !ctx.viewer?.database || !ctx.history) return [];
    if (entityIds.length === 0) return [];

    const module = ctx.viewer.module;
    const db = ctx.viewer.database;

    // Find associated hatches for each entity
    // Hatches are filtered from selection, so we need to include them manually
    const hatchManager = HatchReactorManager.getInstance(module);
    const boundaryToHatches = new Map<string, DrawingModule.OdDbObjectId[]>();
    const allEntitiesToClone: DrawingModule.OdDbObjectId[] = [...entityIds];

    for (const entityId of entityIds) {
      const hatches = hatchManager.getHatchReactorsForBoundary(entityId);
      if (hatches.length > 0) {
        // Store mapping: boundary handle -> hatch IDs
        const handle = entityId.getHandle();
        const handleStr = handle.ascii().toString();
        deleteObj(handle);
        boundaryToHatches.set(handleStr, hatches);

        // Add hatches to clone list
        for (const hatchId of hatches) {
          allEntitiesToClone.push(hatchId);
        }
      }
    }

    // Create translation matrix for offset
    const matrix = createTranslationMatrix(module, offsetX, offsetY);

    // Clone all entities (boundaries + hatches) with transformation
    const clonedEntities = deepCloneEntities(
      module,
      db,
      allEntitiesToClone,
      matrix,
    );

    // Cleanup matrix
    deleteObj(matrix);

    // Build a mapping from old handles to new entity IDs
    const oldHandleToNewId = new Map<string, DrawingModule.OdDbObjectId>();
    for (
      let i = 0;
      i < allEntitiesToClone.length && i < clonedEntities.length;
      i++
    ) {
      const oldHandle = allEntitiesToClone[i].getHandle();
      const oldHandleStr = oldHandle.ascii().toString();
      deleteObj(oldHandle);
      oldHandleToNewId.set(oldHandleStr, clonedEntities[i].entityId);
    }

    // Fix up hatch-boundary associations for cloned hatches
    for (const [oldBoundaryHandle, oldHatchIds] of boundaryToHatches) {
      const newBoundaryId = oldHandleToNewId.get(oldBoundaryHandle);
      if (!newBoundaryId) continue;

      for (const oldHatchId of oldHatchIds) {
        const oldHatchHandle = oldHatchId.getHandle();
        const oldHatchHandleStr = oldHatchHandle.ascii().toString();
        deleteObj(oldHatchHandle);

        const newHatchId = oldHandleToNewId.get(oldHatchHandleStr);
        if (!newHatchId) continue;

        // Sync the cloned hatch to use the cloned boundary
        hatchManager.syncHatch(newHatchId, newBoundaryId);
        // Register the reactor relationship
        hatchManager.registerReactor(newBoundaryId, newHatchId);
      }
    }

    // Register undo commands for each cloned entity
    for (const cloned of clonedEntities) {
      const command = new AddEntityCommand(ctx.viewer, {
        entityHandle: getEntityHandle(cloned.entityId),
        originalLayerName: cloned.layerName,
      });
      ctx.history.execute(command);
    }

    return clonedEntities;
  }

  /**
   * Duplicate all selected entities using ODA deepCloneObjects.
   * Uses AddEntityCommand for undo/redo support.
   * Also handles associated hatches (which are filtered from selection).
   * @param offset Optional offset for duplicated entities (default: 1/4 of extent)
   */
  duplicateSelected(offset?: { x: number; y: number }): void {
    const ctx = this._selectionSet.getContext();
    if (!ctx || !ctx.viewer?.database || !ctx.history) return;

    const entityIds = this._selectionSet.getAll();
    if (entityIds.length === 0) return;

    const module = ctx.viewer.module;

    // Dehighlight current selection
    this._selectionSet.highlightAll(false);

    // Calculate offset (default: 1/4 of combined extents)
    let offsetX = offset?.x;
    let offsetY = offset?.y;

    if (offsetX === undefined || offsetY === undefined) {
      const extents = getCombinedExtents(module, entityIds);
      if (extents) {
        offsetX = offsetX ?? extents.width / 4;
        offsetY = offsetY ?? extents.height / 4;
      } else {
        offsetX = offsetX ?? 10;
        offsetY = offsetY ?? 10;
      }
    }

    const clonedEntities = this.cloneEntitiesAtOffset(
      entityIds,
      offsetX,
      offsetY,
    );

    // Clear selection of original entities and select the new cloned entities
    // Only select non-hatch entities (hatches are filtered from selection anyway)
    this._selectionSet.clear();

    const hatchDesc = module.OdDbHatch.desc();
    for (const cloned of clonedEntities) {
      const pObj = cloned.entityId.safeOpenObject(0, false);
      const isHatch = pObj.isA().isEqualTo(hatchDesc);
      deleteObj(pObj);

      if (!isHatch) {
        this._selectionSet.add(cloned.entityId, false);
      }
    }

    // Highlight and update grid once after all entities added
    this._selectionSet.highlightAll(true);
    this._selectionSet.updateGrid();

    // Update view
    ctx.viewer.update();
  }

  /**
   * Change the layer of all selected entities.
   * @param layerName The name of the layer to move entities to
   */
  setLayer(layerName: string): void {
    const ctx = this._selectionSet.getContext();
    if (!ctx || !ctx.viewer?.database) return;

    const entityIds = this._selectionSet.getAll();
    if (entityIds.length === 0) return;

    const module = ctx.viewer.module;
    const db = ctx.viewer.database;

    // Ensure layer exists
    ensureLayerExists(module, db, layerName);

    const layerNameOd = new module.OdString(layerName);
    const reactorManager = HatchReactorManager.getInstance(module);

    for (const entityId of entityIds) {
      if (entityId && !entityId.isNull()) {
        const entity = openAs(entityId, module.OdDbEntity, 1); // kForWrite
        entity.setLayer(layerNameOd, false, true);
        deleteObj(entity);

        // Cascade layer change to linked hatches
        const hatches = reactorManager.getHatchReactorsForBoundary(entityId);
        for (const hatchId of hatches) {
          const hatchEntity = openAs(hatchId, module.OdDbEntity, 1);
          hatchEntity.setLayer(layerNameOd, false, true);
          deleteObj(hatchEntity);
        }
      }
    }

    deleteObj(layerNameOd);

    // Update view
    ctx.viewer.update();
  }

  // ==================== Lifecycle ====================

  /**
   * Called when the file is closed.
   * Clears selection but keeps the service instance.
   */
  onFileClose(): void {
    this._selectionSet.clear();
    this._isContextSet = false;
  }

  /**
   * Dispose of the service.
   * Clears selection and all handlers.
   */
  dispose(): void {
    this._selectionSet.dispose();
    this._changeHandlers.clear();
    this._isContextSet = false;
  }
}

/**
 * Singleton instance for convenient import.
 */
export const selectionService = SelectionService.getInstance();
